import { RouteTrackingConversionEvent } from "app/config/route-tracking-config";
import { configureMetaPixel } from "app/utils/meta-pixel";
import { getRouteTrackingConversionEvent } from "app/utils/route-tracking";

type FunnelPayloadValue = string | number | boolean | null;
type FunnelPayload = Record<string, FunnelPayloadValue>;

type FunnelWindow = Window &
  typeof globalThis & {
    gtag?: (command: "event", eventName: string, payload: FunnelPayload) => void;
    dataLayer?: Array<Record<string, FunnelPayloadValue>>;
    twq?: (command: "event", eventName: string, payload?: FunnelPayload) => void;
    fbq?: (command: "track", eventName: string, payload?: FunnelPayload) => void;
  };

const firedEvents = new Set<string>();

const toNullableNumber = (value: number | string | null | undefined) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizePaymentToken = (paymentToken: string | null | undefined) => {
  if (!paymentToken) {
    return null;
  }

  const symbolMatch = paymentToken.trim().match(/\b(USDT|USDC)\b/i);
  if (symbolMatch) {
    return symbolMatch[1].toUpperCase();
  }

  const trimmedToken = paymentToken.trim();
  return trimmedToken ? trimmedToken.toUpperCase() : null;
};

const getWindow = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return window as FunnelWindow;
};

const trackIdoFunnelEvent = (
  eventName: string,
  payload: FunnelPayload,
  dedupeKey: string,
  afterTrack?: () => void
) => {
  if (firedEvents.has(dedupeKey)) {
    return false;
  }

  firedEvents.add(dedupeKey);

  const analyticsWindow = getWindow();
  if (!analyticsWindow) {
    return true;
  }

  if (typeof analyticsWindow.gtag === "function") {
    analyticsWindow.gtag("event", eventName, payload);
  }

  analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
  analyticsWindow.dataLayer.push({
    event: eventName,
    ...payload,
  });

  afterTrack?.();

  return true;
};

const trackXConversion = (
  conversionEvent: RouteTrackingConversionEvent | null,
  payload?: FunnelPayload
) => {
  if (!conversionEvent) {
    return;
  }

  const analyticsWindow = getWindow();
  if (typeof analyticsWindow?.twq !== "function") {
    return;
  }

  try {
    if (payload === undefined) {
      analyticsWindow.twq("event", conversionEvent.eventId);
      return;
    }

    analyticsWindow.twq("event", conversionEvent.eventId, payload);
  } catch {
    // X Ads tracking must never interrupt app flow or other analytics.
  }
};

const trackMetaPixelEvent = (
  conversionEvent: RouteTrackingConversionEvent | null,
  payload: FunnelPayload
) => {
  if (!conversionEvent) {
    return;
  }

  if (conversionEvent.pixelId) {
    configureMetaPixel(conversionEvent.pixelId);
  }

  const analyticsWindow = getWindow();
  if (typeof analyticsWindow?.fbq !== "function") {
    return;
  }

  try {
    analyticsWindow.fbq("track", conversionEvent.eventId, payload);
  } catch {
    // Meta Pixel tracking must never interrupt app flow or other analytics.
  }
};

export const trackWalletConnectSuccess = ({
  idoId,
  walletAddress,
  chainId,
}: {
  idoId?: string;
  walletAddress?: string;
  chainId?: number | null;
}) => {
  if (!idoId || !walletAddress) {
    return false;
  }

  return trackIdoFunnelEvent(
    "wallet_connect_success",
    {
      ido_id: idoId,
      page_type: "ido",
      wallet_address_hash: null,
      chain_id: toNullableNumber(chainId),
      wallet_connected: true,
    },
    `wallet_connect_success:${idoId}:${walletAddress.toLowerCase()}`,
    () => {
      const page = { type: "ido" as const, id: idoId };

      trackXConversion(
        getRouteTrackingConversionEvent(
          page,
          "wallet_connect_success",
          "x"
        )
      );
      trackMetaPixelEvent(
        getRouteTrackingConversionEvent(
          page,
          "wallet_connect_success",
          "meta"
        ),
        {
          content_name: "wallet_connect_success",
          page_type: "ido",
          ido_id: idoId,
        }
      );
    }
  );
};

export const trackSaleSuccess = ({
  idoId,
  transactionId,
  chainId,
  paymentToken,
  contributionAmount,
  valueUsd,
  tokensToReceive,
}: {
  idoId?: string;
  transactionId?: string | null;
  chainId?: number | null;
  paymentToken?: string | null;
  contributionAmount?: number | string | null;
  valueUsd?: number | string | null;
  tokensToReceive?: number | string | null;
}) => {
  if (!idoId || !transactionId) {
    return false;
  }

  const normalizedContributionAmount = toNullableNumber(contributionAmount);
  const normalizedValueUsd =
    toNullableNumber(valueUsd) ?? normalizedContributionAmount;

  return trackIdoFunnelEvent(
    "sale_success",
    {
      ido_id: idoId,
      page_type: "ido",
      wallet_address_hash: null,
      transaction_id: transactionId,
      chain_id: toNullableNumber(chainId),
      payment_token: normalizePaymentToken(paymentToken),
      contribution_amount: normalizedContributionAmount,
      value_usd: normalizedValueUsd,
      currency: "USD",
      tokens_to_receive: toNullableNumber(tokensToReceive),
      sale_stage: "live_sale",
    },
    `sale_success:${idoId}:${transactionId}`,
    () => {
      const page = { type: "ido" as const, id: idoId };
      const conversionEvent = getRouteTrackingConversionEvent(
        page,
        "sale_success",
        "x"
      );
      const currency = conversionEvent?.currency ?? "USD";
      const payload: FunnelPayload = { currency };

      if (conversionEvent?.includeValue && normalizedValueUsd !== null) {
        payload.value = normalizedValueUsd;
      }

      trackXConversion(conversionEvent, payload);

      const metaPayload: FunnelPayload = {
        content_name: "sale_success",
        page_type: "ido",
        ido_id: idoId,
        transaction_id: transactionId,
        currency: "USD",
      };

      if (normalizedValueUsd !== null) {
        metaPayload.value = normalizedValueUsd;
      }

      trackMetaPixelEvent(
        getRouteTrackingConversionEvent(page, "sale_success", "meta"),
        metaPayload
      );
    }
  );
};

export const resetIdoFunnelTrackingForTests = () => {
  firedEvents.clear();
};
